import { FC, useEffect, useState } from "react"
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Document } from "@/types" // Assuming Document type includes needed fields
import { PDFViewer } from "../pdf/PDFViewer"
import { AudioViewer } from "../audio/AudioViewer"
import { Download } from "lucide-react"
import { auth } from "@/lib/firebase"
import { useURLBlobFromUrl } from "@/hooks/useURLBlobFromUrl"
import { formatFileSize } from "@/utils/formatFileSize"

interface DocumentViewerModalProps {
	isOpen: boolean
	onClose: () => void
	document: Document | null // Pass the whole document object for now
	caseId: string | null
}

const SUPPORTED_VIEWABLE_EXTENSIONS = [
	"pdf",
	"docx",
	"jpg",
	"jpeg",
	"png",
	"gif",
	"bmp",
	"webp",
	"mp3",
	"wav",
	"ogg",
	"m4a",
	"aac",
	"flac",
]

/**
 * Checks if a document extension corresponds to a file type that can be previewed in the modal.
 * @param fileExtension - The file extension (e.g., 'pdf', 'jpg').
 * @returns True if the file type is viewable, false otherwise.
 */
export const isDocumentViewable = (
	fileExtension: string | null | undefined
): boolean => {
	if (!fileExtension) {
		return false
	}
	return SUPPORTED_VIEWABLE_EXTENSIONS.includes(fileExtension.toLowerCase())
}

export const DocumentViewerModal: FC<DocumentViewerModalProps> = ({
	isOpen,
	onClose,
	document,
	caseId,
}) => {
	if (!isOpen || !document || !caseId) {
		return null
	}

	const viewUrl = `${
		import.meta.env.VITE_API_URL
	}/api/cases/${caseId}/documents/${document.id}/view/`

	const { data: blobUrl, isLoading } = useURLBlobFromUrl(viewUrl)

	const renderContent = () => {
		// Determine file type based on extension
		const fileExt = document.file_extension?.toLowerCase() || ""

		// Render PDF viewer for PDF files
		if (fileExt === "pdf") {
			return (
				<div className="w-full h-full overflow-visible">
					<PDFViewer url={viewUrl} />
				</div>
			)
		}

		// Handle DOCX files
		if (fileExt === "docx") {
			// For DOCX files, check if the server is returning a PDF (converted from DOCX)
			// by making a HEAD request to check the Content-Type
			const [isPdfAvailable, setIsPdfAvailable] = useState<boolean>(true)

			useEffect(() => {
				const checkDocumentType = async () => {
					try {
						const user = auth.currentUser
						if (!user) {
							setIsPdfAvailable(false)
							return
						}
						const token = await user.getIdToken()
						const response = await fetch(viewUrl, {
							method: "HEAD",
							headers: { Authorization: `Bearer ${token}` },
						})
						const contentType = response.headers.get("Content-Type")
						setIsPdfAvailable(contentType === "application/pdf")
					} catch (error) {
						console.error("Error checking document type:", error)
						setIsPdfAvailable(false)
					}
				}

				checkDocumentType()
			}, [viewUrl])

			if (!isPdfAvailable) {
				return (
					<div className="mt-4 p-4 border rounded-md min-h-[60vh] flex flex-col items-center justify-center bg-muted">
						<p className="text-muted-foreground">
							PDF preview not available for this DOCX file
						</p>
						{blobUrl ? (
							<a
								href={blobUrl}
								download={document.name}
								className="mt-4 px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90"
							>
								Download Original File
							</a>
						) : (
							<Button disabled className="mt-4">
								Loading File...
							</Button>
						)}
					</div>
				)
			}

			return (
				<div className="w-full h-full overflow-visible">
					<PDFViewer url={viewUrl} />
				</div>
			)
		}

		// Handle image files
		if (["jpg", "jpeg", "png", "gif", "bmp", "webp"].includes(fileExt)) {
			if (isLoading) {
				return (
					<div className="flex items-center justify-center p-4">
						<p>Loading image...</p>
					</div>
				)
			}

			return (
				<div className="flex items-center justify-center p-4">
					<img
						src={blobUrl}
						alt={document.name}
						className="max-w-full max-h-[70vh] object-contain"
					/>
				</div>
			)
		}

		// Handle audio files
		if (["mp3", "wav", "ogg", "m4a", "aac", "flac"].includes(fileExt)) {
			return (
				<AudioViewer
					url={viewUrl}
					fileName={document.name}
					caseId={caseId}
					documentId={document.id}
				/>
			)
		}

		// Default placeholder for other file types
		return (
			<div className="mt-4 p-4 border rounded-md min-h-[60vh] flex flex-col items-center justify-center bg-muted">
				<p className="text-muted-foreground">
					No preview available for {document.name}
				</p>
				<p className="text-muted-foreground text-xs mt-2">
					File type: {fileExt.toUpperCase()}
				</p>
				{blobUrl ? (
					<a
						href={blobUrl}
						download={document.name}
						className="mt-4 px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90"
					>
						Download File
					</a>
				) : (
					<Button disabled className="mt-4">
						Loading File...
					</Button>
				)}
			</div>
		)
	}

	return (
		<Dialog open={isOpen} onOpenChange={onClose}>
			<DialogContent className="max-w-5xl w-[95vw] h-[90vh] max-h-[90vh] flex flex-col">
				<DialogHeader className="pb-2">
					<DialogTitle className="truncate" title={document.name}>
						{document.name}
					</DialogTitle>
					<DialogDescription>
						{document.file_extension?.toUpperCase()} File (
						{document.size ? formatFileSize(document.size) : "Size unknown"})
					</DialogDescription>
				</DialogHeader>
				<div className="flex-grow overflow-auto">{renderContent()}</div>
				<DialogFooter className="mt-2 pt-2 border-t">
					<div className="flex items-center space-x-2 w-full justify-between">
						{blobUrl ? (
							<a
								href={blobUrl}
								download={document.name}
								className="flex items-center space-x-1 px-3 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
							>
								<Download size={16} />
								<span>Download</span>
							</a>
						) : (
							<Button disabled className="flex items-center space-x-1">
								<Download size={16} />
								<span>Loading...</span>
							</Button>
						)}
						<Button variant="outline" onClick={onClose}>
							Close
						</Button>
					</div>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
